import { useCallback, useEffect, useId, useState } from "react";
import Button from "../components/Button";
import CopyButton from "../components/CopyButton";
import Drawer from "../components/Drawer";
import Modal from "../components/Modal";
import SectionHeader from "../components/SectionHeader";
import Tag from "../components/Tag";
import { REJECTION_REASONS, REWARD_NETWORK, REWARD_STATUS } from "./referralsData";
import drawerStyles from "./AccountDrawer.module.css";
import styles from "./RewardDrawer.module.css";

const TX_HASH_PATTERN = /^0x[0-9a-fA-F]{64}$/;

export function formatTimestamp(date) {
  const day = date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${day}, ${time}`;
}

function Field({ label, children, wide = false }) {
  return (
    <div className={`${drawerStyles.field} ${wide ? drawerStyles.fieldWide : ""}`}>
      <p className={`${drawerStyles.fieldLabel} label-font`}>{label}</p>
      <div className={`${drawerStyles.fieldValue} d-flex align-items-center`}>{children}</div>
    </div>
  );
}

// onConfirm resolves to { error } when the API refuses, which is shown under the form
function useSubmit(open, onConfirm) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const submit = async (value) => {
    setSubmitting(true);
    setError(null);
    const result = await onConfirm(value);
    if (result?.error) {
      setError(result.error);
      setSubmitting(false);
    }
  };

  return { submitting, error, submit };
}

function MarkPaidModal({ request, noun, open, onClose, onConfirm }) {
  const inputId = useId();
  const [txHash, setTxHash] = useState("");
  const { submitting, error, submit } = useSubmit(open, onConfirm);

  useEffect(() => {
    if (open) {
      setTxHash("");
    }
  }, [open]);

  const value = txHash.trim();
  const valid = TX_HASH_PATTERN.test(value);

  return (
    <Modal open={open} title={`Mark ${noun} as paid`} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && !submitting) {
            submit(value);
          }
        }}
      >
        <div className={styles.summary}>
          <div className={`${styles.summaryRow} label-font d-flex justify-content-between`}>
            <span>Amount</span>
            <span className={styles.summaryAmount}>${request.amount} USDC</span>
          </div>
          <div className={`${styles.summaryRow} label-font d-flex justify-content-between`}>
            <span>Network</span>
            <span>{REWARD_NETWORK}</span>
          </div>
          <div>
            <p className={`${styles.summaryRow} label-font`}>USDC ERC-20 address</p>
            <div className={`${styles.wallet} d-flex align-items-center justify-content-between`}>
              <span>{request.address}</span>
              <CopyButton value={request.address} label="Copy wallet address" />
            </div>
          </div>
        </div>

        <div className={styles.hashField}>
          <label htmlFor={inputId} className={styles.hashLabel}>
            Transaction hash<span className={styles.required}>*</span>
          </label>
          <input
            id={inputId}
            className={styles.hashInput}
            autoFocus
            spellCheck={false}
            autoComplete="off"
            value={txHash}
            onChange={(event) => setTxHash(event.target.value)}
            aria-invalid={value !== "" && !valid}
          />
          {value ? (
            <p className={valid ? styles.feedbackValid : styles.feedbackInvalid}>
              {valid ? "Valid Ethereum transaction hash" : "Enter a 0x-prefixed, 64-character transaction hash"}
            </p>
          ) : null}
        </div>

        {error ? <p className={styles.feedbackInvalid}>{error}</p> : null}

        <div className={`${styles.ctas} d-flex`}>
          <Button type="button" variant="ghost" className={styles.cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!valid || submitting}>
            {submitting ? "Saving…" : "Confirm payment"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RejectModal({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState(null);
  const { submitting, error, submit } = useSubmit(open, onConfirm);

  useEffect(() => {
    if (open) {
      setReason(null);
    }
  }, [open]);

  return (
    <Modal open={open} title="Select rejection reason" onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          if (reason && !submitting) {
            submit(reason);
          }
        }}
      >
        <fieldset className={styles.reasons}>
          <legend className="visually-hidden">Rejection reason</legend>
          {REJECTION_REASONS.map((option) => (
            <label key={option} className={`${styles.reason} d-flex align-items-center`}>
              <input
                type="radio"
                name="rejection-reason"
                className={styles.radio}
                checked={reason === option}
                onChange={() => setReason(option)}
              />
              {option}
            </label>
          ))}
        </fieldset>

        {error ? <p className={styles.feedbackInvalid}>{error}</p> : null}

        <div className={`${styles.ctas} d-flex`}>
          <Button type="button" variant="ghost" className={styles.cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!reason || submitting}>
            {submitting ? "Saving…" : "Confirm rejection"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// The review drawer for a USDC withdrawal request: referral rewards, and trader payouts
// (`noun` "payout", with the payout's own figures passed as `details`)
export default function RewardDrawer({
  request,
  onClose,
  onMarkPaid,
  onReject,
  title = "Reward payout request",
  noun = "reward",
  details = [],
}) {
  const [modal, setModal] = useState(null); // "paid" | "reject" | null

  useEffect(() => {
    setModal(null);
  }, [request?.id]);

  const closeModal = useCallback(() => setModal(null), []);
  // While a modal is up, Escape should only close the modal
  const closeDrawer = useCallback(() => {
    if (!modal) {
      onClose();
    }
  }, [modal, onClose]);

  if (!request) {
    return null;
  }

  const status = REWARD_STATUS[request.status];
  const isOpenRequest = request.status === "requested";
  const processedVerb = request.status === "paid" ? "marked as paid" : "rejected";

  return (
    <>
      <Drawer
        open
        title={title}
        onClose={closeDrawer}
        footer={
          isOpenRequest ? (
            <>
              <Button variant="ghost" className={styles.footerButton} onClick={() => setModal("reject")}>
                Reject
              </Button>
              <Button variant="ghost" className={styles.footerButton} onClick={() => setModal("paid")}>
                Mark as paid
              </Button>
            </>
          ) : null
        }
      >
        <div className={drawerStyles.sections}>
          <section>
            <SectionHeader title="Request overview" />
            <div className={drawerStyles.fieldGrid}>
              <Field label="Email">{request.email}</Field>
              <Field label="Request ID">
                {request.reference}
                <CopyButton value={request.reference} label="Copy request ID" />
              </Field>
              <Field label="Requested amount">${request.amount} USDC</Field>
              {details.map(({ label, value }) => (
                <Field key={label} label={label}>
                  {value}
                </Field>
              ))}
              {/* Payouts have no balance check to show (eligible is null) */}
              {isOpenRequest && request.eligible != null ? (
                <Field label="Request verification">
                  {/* Ineligible: the trader's earned balance no longer covers their requests */}
                  {request.eligible ? <Tag tone="success">Eligible</Tag> : <Tag tone="danger">Exceeds balance</Tag>}
                </Field>
              ) : (
                <Field label="Status">
                  <Tag tone={status.tone}>{status.label}</Tag>
                </Field>
              )}
              <Field label="USDC ERC-20 address" wide>
                {request.address}
                <CopyButton value={request.address} label="Copy wallet address" />
              </Field>
            </div>
          </section>

          {isOpenRequest ? null : (
            <section>
              <SectionHeader title="Request timeline" />
              <ol className={drawerStyles.timeline}>
                <li>
                  <p className={drawerStyles.timelineLabel}>Request submitted by the user</p>
                  <p className={drawerStyles.timelineAt}>{formatTimestamp(request.requestedAt)}</p>
                </li>
                <li>
                  <p className={drawerStyles.timelineLabel}>
                    Request {processedVerb} by {request.processedBy}
                  </p>
                  <p className={drawerStyles.timelineAt}>{formatTimestamp(request.processedAt)}</p>
                </li>
              </ol>
            </section>
          )}

          {request.status === "paid" ? (
            <section>
              <SectionHeader title="Transaction hash" />
              <p className={styles.detail}>{request.txHash}</p>
            </section>
          ) : null}

          {request.status === "rejected" ? (
            <section>
              <h2 className={`${styles.rejectionTitle} label-font`}>Rejection reason</h2>
              <p className={styles.detail}>{request.rejectionReason}</p>
            </section>
          ) : null}
        </div>
      </Drawer>

      <MarkPaidModal
        request={request}
        noun={noun}
        open={modal === "paid"}
        onClose={closeModal}
        onConfirm={async (txHash) => {
          const result = await onMarkPaid(request.id, txHash);
          if (!result.error) setModal(null);
          return result;
        }}
      />
      <RejectModal
        open={modal === "reject"}
        onClose={closeModal}
        onConfirm={async (reason) => {
          const result = await onReject(request.id, reason);
          if (!result.error) setModal(null);
          return result;
        }}
      />
    </>
  );
}
